import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const LOGO_URL = "/logo.png";
const LOGO_COMPACT_URL = "/logo-compact.png";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  
  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/agence", label: "Notre Agence" },
    { href: "/catalogue", label: "Nos Biens" },
    { href: "/gestion", label: "Faire Gérer Son Bien" },
    { href: "/documents", label: "Déposer un Dossier" },
    { href: "/contact", label: "Contact" },
  ];
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <>
      {/* Top bar */}
      <div className="bg-[#0A0A0A] text-white py-2 text-sm hidden md:block">
        <div className="container-custom flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`tel:${settings.phone?.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Phone size={14} />
              <span>{settings.phone}</span>
            </a>
            <a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Mail size={14} />
              <span>{settings.email}</span>
            </a>
          </div>
          <Link 
            to="/admin/login" 
            className="text-gray-400 hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-wider"
            data-testid="admin-login-link"
          >
            Espace Pro
          </Link>
        </div>
      </div>
      
      {/* Main header */}
      <header className="glass sticky top-0 z-40 border-b border-gray-100">
        <div className="container-custom py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="header-logo">
              <img 
                src={LOGO_COMPACT_URL} 
                alt="MAINGUY IMMOBILIER" 
                className="h-16 md:h-20 w-auto object-contain"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-5 xl:gap-8 whitespace-nowrap" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link text-xs xl:text-sm tracking-wider xl:tracking-widest ${isActive(link.href) ? 'text-[#D4AF37]' : 'text-gray-800 hover:text-[#D4AF37]'}`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            
            {/* CTA Button */}
            <div className="hidden xl:block flex-shrink-0 whitespace-nowrap">
              <Link 
                to="/estimation" 
                className="btn-primary"
                data-testid="header-cta-button"
              >
                Estimer mon bien
              </Link>
            </div>
            
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={28} className="text-[#0A0A0A]" />
            </button>
          </div>
        </div>
      </header>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X size={32} className="text-white" />
          </button>
          
          <img 
            src={LOGO_URL} 
            alt="MAINGUY IMMOBILIER" 
            className="h-20 w-auto mb-6 bg-white rounded-lg p-2"
          />
          
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="mobile-nav-link"
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </Link>
          ))}

          <Link
            to="/estimation"
            className="btn-primary mt-6 w-full max-w-xs text-center"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-cta-estimation"
          >
            Estimer mon bien
          </Link>
          
          <Link 
            to="/admin/login" 
            className="text-gray-500 text-sm uppercase tracking-wider mt-8 hover:text-[#D4AF37]"
            onClick={() => setMobileMenuOpen(false)}
          >
            Espace Pro
          </Link>
        </div>
      )}
    </>
  );
};

export default Header;
